import { Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import Highcharts, { SeriesOptionsType } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { capitalize, merge } from "lodash";
import React, { useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import { PaymentMethod, Product, Source, SourceCopy } from "types";
import shallow from "zustand/shallow";
import { PieOptions } from "./Base/PieOptions";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useAutoFetchPlugins } from "@/hooks/usePlugins";
import { LocalizationProps } from "@/models/Locale";
import { getPaymentMethodName } from "@/models/Payment";
import {
    getChannelsReport,
    getListingsReport,
    getPaymentsReport,
    PaymentAndChannelsReportParams,
} from "@/reports/PaymentChannelsListingsReport";
import { useAnalyticsFilterStore } from "@/stores/reports/analyticsFilterStore";

export const PaymentMethodsPie = () => {
    const seller = useSeller();
    const getName = (key: PaymentMethod) => getPaymentMethodName(seller, key) ?? capitalize(key);
    const params = { terms: "method", stats: "items.gross", getName };

    return (
        <PieChart
            title="Payment Methods"
            prefix="paymentMethods"
            params={params}
            getName={getName}
            fetch={getPaymentsReport}
        />
    );
};

export const ChannelsPie = () => {
    const params = { terms: "source", stats: "items.gross", getName };

    return <PieChart title="Channels" prefix="source" params={params} getName={getName} fetch={getChannelsReport} />;
};

const getName = (key: Source) => SourceCopy[key] ?? capitalize(key);

export const ProductsPie = () => {
    const getName = useProductName();
    const { isLoading, hasNextPage, isFetchingNextPage } = useAutoFetchCollection(["products"], {
        deleted: true,
    });

    if (isLoading || hasNextPage || isFetchingNextPage) {
        // We need to wait for products to load so that the pie chart can show the legend properly
        return <Skeleton height={200} classNames={{ container: "mx-2" }} />;
    }

    const params = { terms: "items.orderItem.product.id", stats: "items.gross" };

    return (
        <PieChart
            title="Products"
            localize={false}
            prefix="products"
            params={params}
            fetch={getListingsReport}
            getName={getName}
        />
    );
};

const useProductName = () => {
    const queryClient = useQueryClient();
    return useCallback((id: ID) => queryClient.getQueryData<Product>(["products", id])?.name ?? id, [queryClient]);
};

interface PieChartProps extends LocalizationProps {
    title: string;
    prefix: string;
    params: { terms: string; stats: string };
    getName?: (key: any) => string;
    fetch: (params: PaymentAndChannelsReportParams) => Promise<SeriesOptionsType[]>;
}

const PieChart = ({ title, prefix, params, getName, fetch, localize = true }: PieChartProps) => {
    const seller = useSeller();
    const { data: plugins = [] } = useAutoFetchPlugins();

    const [from, to] = useAnalyticsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const filters = useAnalyticsFilterStore((state) => state.filters);
    const productList: ID[] = filters.products.all ? [] : filters.products.selected ?? [];

    const finalParams = { ...params, seller, mode: filters.mode, products: productList, from, to, getName, plugins };
    const { data, isLoading } = useQuery<SeriesOptionsType[]>(["pieChart", prefix, finalParams], async () =>
        fetch(finalParams),
    );

    if (isLoading) {
        return <Skeleton height={200} classNames={{ container: "mx-2" }} />;
    }

    return (
        <div data-testid="pie-chart">
            {/* Div is required to wrap the SVG for rendering height properly */}
            <HighchartsReact highcharts={Highcharts} options={getChartOptions(title, data, localize)} />
        </div>
    );
};

const getChartOptions = (title: string, data: any, localize = true): Highcharts.Options => {
    const options: Highcharts.Options = {
        legend: { className: clsx({ "no-translate": !localize }) },
        series: [{ type: "pie", data }],
        title: { text: title, align: "left" },
    };
    return merge({}, PieOptions, options);
};
